import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Switch,
  Popover,
  Typography,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Box,
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { HandoffField } from '../../services/handoffApi';

const capitalizeFirstLetter = (str: string) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

interface SortableRowProps {
  field: HandoffField;
  showErrors?: boolean;
  onUpdate: (id: string, updates: Partial<HandoffField>) => void;
  onRemove: (id: string) => void;
  onFocus?: () => void;
}

const SortableRow: React.FC<SortableRowProps> = ({ field, showErrors, onUpdate, onRemove, onFocus }) => {
  const [labelAnchorEl, setLabelAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [descAnchorEl, setDescAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [exampleAnchorEl, setExampleAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
    position: 'relative' as const,
    backgroundColor: isDragging ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell sx={{ width: 50 }}>
        <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab' }}>
          <DragIcon fontSize="small" />
        </IconButton>
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          fullWidth
          value={field.label}
          onFocus={onFocus}
          onChange={(e) => onUpdate(field.id, { label: capitalizeFirstLetter(e.target.value) })}
          onClick={(e) => {
            const input = e.currentTarget.querySelector('input');
            if (input && input.scrollWidth > input.clientWidth) {
              setLabelAnchorEl(e.currentTarget as any);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              (e.target as HTMLInputElement).blur();
            }
          }}
          placeholder="The text the chatbot asks the customer"
          error={showErrors && !field.label.trim()}
          sx={{
            '& .MuiInputBase-root': {
              cursor: 'text',
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'text.secondary',
              opacity: 0.8,
            },
          }}
        />
        <Popover
          open={Boolean(labelAnchorEl)}
          anchorEl={labelAnchorEl}
          onClose={() => setLabelAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: { p: 2, width: 350 },
          }}
        >
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            label="Edit Question"
            value={field.label}
            onChange={(e) => onUpdate(field.id, { label: capitalizeFirstLetter(e.target.value) })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                setLabelAnchorEl(null);
              }
            }}
            autoFocus
          />
        </Popover>
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          fullWidth
          value={field.description}
          onFocus={onFocus}
          onChange={(e) => onUpdate(field.id, { description: capitalizeFirstLetter(e.target.value) })}
          onClick={(e) => {
            const input = e.currentTarget.querySelector('input');
            if (input && input.scrollWidth > input.clientWidth) {
              setDescAnchorEl(e.currentTarget as any);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              (e.target as HTMLInputElement).blur();
            }
          }}
          placeholder="Explain what this question is about and why it is asked"
          error={showErrors && !field.description.trim()}
          sx={{
            '& .MuiInputBase-root': {
              cursor: 'text',
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'text.secondary',
              opacity: 0.8,
            },
          }}
        />
        <Popover
          open={Boolean(descAnchorEl)}
          anchorEl={descAnchorEl}
          onClose={() => setDescAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: { p: 2, width: 350 },
          }}
        >
          <TextField
            fullWidth
            multiline
            rows={4}
            size="small"
            label="Edit Description"
            value={field.description}
            onChange={(e) => onUpdate(field.id, { description: capitalizeFirstLetter(e.target.value) })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                setDescAnchorEl(null);
              }
            }}
            autoFocus
          />
        </Popover>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{field.type}</Typography>
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          fullWidth
          value={field.example}
          onFocus={onFocus}
          onChange={(e) => onUpdate(field.id, { example: capitalizeFirstLetter(e.target.value) })}
          onClick={(e) => {
            const input = e.currentTarget.querySelector('input');
            if (input && input.scrollWidth > input.clientWidth) {
              setExampleAnchorEl(e.currentTarget as any);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              (e.target as HTMLInputElement).blur();
            }
          }}
          placeholder="Hint for the customer"
          error={showErrors && !field.example.trim()}
          sx={{
            '& .MuiInputBase-root': {
              cursor: 'text',
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'text.secondary',
              opacity: 0.8,
            },
          }}
        />
        <Popover
          open={Boolean(exampleAnchorEl)}
          anchorEl={exampleAnchorEl}
          onClose={() => setExampleAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: { p: 2, width: 350 },
          }}
        >
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            label="Edit Example"
            value={field.example}
            onChange={(e) => onUpdate(field.id, { example: capitalizeFirstLetter(e.target.value) })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                setExampleAnchorEl(null);
              }
            }}
            autoFocus
          />
        </Popover>
      </TableCell>
      <TableCell>
        <Tooltip
          title="If enabled, the chatbot will require a response before proceeding"
          slotProps={{ tooltip: { sx: { fontSize: '0.875rem', py: 1, px: 1.5 } } }}
        >
          <Switch
            size="small"
            checked={field.required}
            onChange={(e) => onUpdate(field.id, { required: e.target.checked })}
          />
        </Tooltip>
      </TableCell>
      <TableCell sx={{ width: 50 }}>
        <IconButton size="small" color="error" onClick={() => onRemove(field.id)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const MobileSortableRow: React.FC<SortableRowProps> = ({ field, showErrors, onUpdate, onRemove, onFocus }) => {
  const [labelAnchorEl, setLabelAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [descAnchorEl, setDescAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [exampleAnchorEl, setExampleAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : 1,
    opacity: isDragging ? 0.6 : 1,
    marginBottom: '16px',
    listStyle: 'none',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card variant="outlined" sx={{ position: 'relative', boxShadow: isDragging ? 4 : 0 }}>
        <Box sx={{ p: 1, display: 'flex', alignItems: 'center', bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
          <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab', mr: 1 }}>
            <DragIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle2" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Question {field.order}
          </Typography>
          <Tooltip
            title="If enabled, the chatbot will require a response before proceeding"
            enterTouchDelay={0}
            leaveTouchDelay={5000}
            slotProps={{ tooltip: { sx: { fontSize: '0.875rem', py: 1, px: 1.5 } } }}
          >
            <Switch
              size="small"
              checked={field.required}
              onChange={(e) => onUpdate(field.id, { required: e.target.checked })}
            />
          </Tooltip>
          <IconButton size="small" color="error" onClick={() => onRemove(field.id)} sx={{ ml: 1 }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>
                Question Text
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={field.label}
                onFocus={onFocus}
                onChange={(e) => onUpdate(field.id, { label: capitalizeFirstLetter(e.target.value) })}
                onClick={(e) => {
                  const input = e.currentTarget.querySelector('input');
                  if (input && input.scrollWidth > input.clientWidth) {
                    setLabelAnchorEl(e.currentTarget as any);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                placeholder="The text the chatbot asks the customer"
                error={showErrors && !field.label.trim()}
                sx={{ '& .MuiInputBase-input': { cursor: 'text' } }}
              />
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>
                Description
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={field.description}
                onFocus={onFocus}
                onChange={(e) => onUpdate(field.id, { description: capitalizeFirstLetter(e.target.value) })}
                onClick={(e) => {
                  const input = e.currentTarget.querySelector('input');
                  if (input && input.scrollWidth > input.clientWidth) {
                    setDescAnchorEl(e.currentTarget as any);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                placeholder="Explain what this question is about"
                error={showErrors && !field.description.trim()}
                sx={{ '& .MuiInputBase-input': { cursor: 'text' } }}
              />
            </Box>

            <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>
                  Answer Type
                </Typography>
                <Typography variant="body2" sx={{ px: 1, py: 0.5, bgcolor: 'action.hover', borderRadius: 1, display: 'inline-block', textTransform: 'capitalize' }}>
                  {field.type}
                </Typography>
              </Box>
              <Box sx={{ flex: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>
                  Example
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  value={field.example}
                  onFocus={onFocus}
                  onChange={(e) => onUpdate(field.id, { example: capitalizeFirstLetter(e.target.value) })}
                  onClick={(e) => {
                    const input = e.currentTarget.querySelector('input');
                    if (input && input.scrollWidth > input.clientWidth) {
                      setExampleAnchorEl(e.currentTarget as any);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  placeholder="Hint for the customer"
                  error={showErrors && !field.example.trim()}
                  sx={{ '& .MuiInputBase-input': { cursor: 'text' } }}
                />
              </Box>
            </Stack>
          </Stack>

          {/* Popovers for editing */}
          <Popover
            open={Boolean(labelAnchorEl)}
            anchorEl={labelAnchorEl}
            onClose={() => setLabelAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { p: 2, width: '90vw', maxWidth: 350 } }}
          >
            <TextField
              fullWidth
              multiline
              rows={2}
              size="small"
              label="Edit Question"
              value={field.label}
              onChange={(e) => onUpdate(field.id, { label: capitalizeFirstLetter(e.target.value) })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  setLabelAnchorEl(null);
                }
              }}
              autoFocus
            />
          </Popover>

          <Popover
            open={Boolean(descAnchorEl)}
            anchorEl={descAnchorEl}
            onClose={() => setDescAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { p: 2, width: '90vw', maxWidth: 350 } }}
          >
            <TextField
              fullWidth
              multiline
              rows={4}
              size="small"
              label="Edit Description"
              value={field.description}
              onChange={(e) => onUpdate(field.id, { description: capitalizeFirstLetter(e.target.value) })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  setDescAnchorEl(null);
                }
              }}
              autoFocus
            />
          </Popover>

          <Popover
            open={Boolean(exampleAnchorEl)}
            anchorEl={exampleAnchorEl}
            onClose={() => setExampleAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { p: 2, width: '90vw', maxWidth: 350 } }}
          >
            <TextField
              fullWidth
              multiline
              rows={2}
              size="small"
              label="Edit Example"
              value={field.example}
              onChange={(e) => onUpdate(field.id, { example: capitalizeFirstLetter(e.target.value) })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  setExampleAnchorEl(null);
                }
              }}
              autoFocus
            />
          </Popover>
        </CardContent>
      </Card>
    </div>
  );
};

interface HandoffConfigEditorProps {
  fields: HandoffField[];
  showErrors?: boolean;
  onFieldsChange: (fields: HandoffField[]) => void;
  onFocus?: () => void;
}

const HandoffConfigEditor: React.FC<HandoffConfigEditorProps> = ({
  fields,
  showErrors,
  onFieldsChange,
  onFocus,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex((f) => f.id === active.id);
      const newIndex = fields.findIndex((f) => f.id === over.id);
      const reordered = arrayMove(fields, oldIndex, newIndex).map((f, i) => ({
        ...f,
        order: i + 1,
      }));
      onFieldsChange(reordered);
    }
  };

  const handleUpdateField = (id: string, updates: Partial<HandoffField>) => {
    onFieldsChange(
      fields.map((f) => (f.id === id ? { ...f, ...updates } : f))
    );
  };

  const handleRemoveField = (id: string) => {
    onFieldsChange(
      fields.filter((f) => f.id !== id).map((f, i) => ({ ...f, order: i + 1 }))
    );
  };

  if (isMobile) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <Box sx={{ mt: 2 }}>
          <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
            {fields.map((field) => (
              <MobileSortableRow
                key={field.id}
                field={field}
                showErrors={showErrors}
                onUpdate={handleUpdateField}
                onRemove={handleRemoveField}
                onFocus={onFocus}
              />
            ))}
          </SortableContext>
          {fields.length === 0 && (
            <Paper variant="outlined" sx={{ py: 4, textAlign: 'center', color: 'text.secondary', bgcolor: 'action.hover' }}>
              No questions added yet.<br/>
              Click "+ Add Questions" to get started.
            </Paper>
          )}
        </Box>
      </DndContext>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <TableContainer component={Paper} elevation={0} variant="outlined">
        <Table size="small">
          <TableHead sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
            <TableRow>
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>
                <Tooltip title="The query the chatbot will ask the customer" slotProps={{ tooltip: { sx: { fontSize: '0.875rem', py: 1, px: 1.5 } } }}>
                  <span style={{ cursor: 'help' }}>Question</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <Tooltip title="Explain what this question is asking and why it’s important for your team" slotProps={{ tooltip: { sx: { fontSize: '0.875rem', py: 1, px: 1.5 } } }}>
                  <span style={{ cursor: 'help' }}>Description</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <Tooltip title="The format of the response expected from the customer" slotProps={{ tooltip: { sx: { fontSize: '0.875rem', py: 1, px: 1.5 } } }}>
                  <span style={{ cursor: 'help' }}>Answer Type</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <Tooltip title="A sample answer to help the customer provide the right information" slotProps={{ tooltip: { sx: { fontSize: '0.875rem', py: 1, px: 1.5 } } }}>
                  <span style={{ cursor: 'help' }}>Example</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <Tooltip title="If enabled, the chatbot will require a response before proceeding" slotProps={{ tooltip: { sx: { fontSize: '0.875rem', py: 1, px: 1.5 } } }}>
                  <span style={{ cursor: 'help' }}>Must Ask?</span>
                </Tooltip>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
              {fields.map((field) => (
                <SortableRow
                  key={field.id}
                  field={field}
                  showErrors={showErrors}
                  onUpdate={handleUpdateField}
                  onRemove={handleRemoveField}
                  onFocus={onFocus}
                />
              ))}
            </SortableContext>
            {fields.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  No questions added yet. Click "+ Add Questions" to get started.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </DndContext>
  );
  ;
};

export default HandoffConfigEditor;
